import React, { Fragment, memo } from 'react';
import { styled, css, cssMap } from '@compiled/react';
import { RelayEnvironmentProvider, useLazyLoadQuery } from 'react-relay';

import { token } from '@atlaskit/tokens';

import { APP_NAV_CONTAINER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { LoadableLazy, LoadableAfterPaint } from '@confluence/loadable';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { useCanPerformSpaceOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { SpaceNavigationRelatedContent } from '@confluence/space-curation';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';

import { CommonSpaceNavigationContent } from './CommonSpaceNavigationContent.tsx';
import { SpaceHeader } from './SpaceHeader.tsx';
import { ContainerSkeleton } from './ContainerSkeleton.tsx';
import { SpaceLinks } from './SpaceLinks.tsx';
import { SPACE_APPS_METRIC } from './perf.config.ts';
import SpaceNavigationQueryArtifact, {
	type SpaceNavigationQuery,
	type SpaceNavigationQuery$data,
} from './__generated__/SpaceNavigationQuery.graphql.ts';

const ErrorView = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ErrorView" */ './ErrorView.tsx')).ErrorView,
});

const SpaceApps = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceApps" */ './SpaceApps.tsx')).SpaceApps,
});

const spaceLinksStyles = css({
	margin: token('space.0'),
	padding: token('space.0'),
	listStyle: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:not(:first-of-type)': {
		marginTop: token('space.0'),
	},
});

const SidebarContent = memo(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled.div({
		paddingBottom: token('space.0'),
		position: 'relative',
		marginTop: 'auto',
	}),
);

const relatedContentStyles = cssMap({
	container: {
		position: 'sticky',
		bottom: 0,
		backgroundColor: token('elevation.surface'),
		paddingBottom: token('space.150'),
		zIndex: 1,
	},
});

const separatorStyles = css({
	marginTop: token('space.150'),
	marginInline: token('space.negative.150'),
	border: token('space.0'),
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

const separatorStylesFallback = css({
	marginTop: token('space.150'),
	marginInline: token('space.negative.150'),
	border: '0',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

type SpaceNavigationContentProps = {
	navigationProps: any;
	errorProps: any;
	spaceDetails: any;
};

type RouteFragmentRefs = {
	space: NonNullable<SpaceNavigationQuery$data['space']>;
	spaceSidebarLinks: NonNullable<SpaceNavigationQuery$data['spaceSidebarLinks']>;
};

export const SpaceNavigationContent = (props: SpaceNavigationContentProps): React.JSX.Element =>
	fg('confluence_space_nav_core_frag_reader') ? (
		<RouteBackedSpaceNavigationContent {...props} />
	) : (
		<LegacySpaceNavigationContent {...props} />
	);

const RouteBackedSpaceNavigationContent = ({
	...props
}: SpaceNavigationContentProps): React.JSX.Element => (
	<RelayEnvironmentProvider environment={getRelayEnvironment()}>
		<RouteBackedSpaceNavigationContentInner {...props} />
	</RelayEnvironmentProvider>
);

const RouteBackedSpaceNavigationContentInner = ({
	navigationProps,
	...props
}: SpaceNavigationContentProps): React.JSX.Element => {
	const routeData = useLazyLoadQuery<SpaceNavigationQuery>(
		SpaceNavigationQueryArtifact,
		navigationProps.routeReaderVariables,
		{ fetchPolicy: 'store-only' },
	);

	if (!routeData.space || !routeData.spaceSidebarLinks) {
		return <LegacySpaceNavigationContent navigationProps={navigationProps} {...props} />;
	}

	return (
		<SpaceNavigationContentLayout
			navigationProps={navigationProps}
			routeFragmentRefs={{
				space: routeData.space,
				spaceSidebarLinks: routeData.spaceSidebarLinks,
			}}
			{...props}
		/>
	);
};

const LegacySpaceNavigationContent = (props: SpaceNavigationContentProps): React.JSX.Element => (
	<SpaceNavigationContentLayout {...props} />
);

const SpaceNavigationContentLayout = ({
	navigationProps,
	errorProps,
	spaceDetails,
	routeFragmentRefs,
}: SpaceNavigationContentProps & {
	routeFragmentRefs?: RouteFragmentRefs;
}): React.JSX.Element => {
	const {
		loading,
		hasSpace,
		space,
		spaceKey,
		homepageId,
		main,
		quick,
		isSpaceAdmin,
		isBlogNavigation,
		blogsHidden,
		pageTreeFinishedLoading,
		onDragDropSuccess,
		showShortcutsSection,
		shouldRenderSpaceApps,
		allAppLinks,
		allVisibleAppLinks,
		loadingForgeApps,
		onContentTreeLoadComplete,
	} = navigationProps;
	const { canAdminister } = spaceDetails;
	const [canCreateContent] = useCanPerformSpaceOperation({
		spaceKey: spaceKey || '',
		operation: OperationTypes.CREATE,
		contentType: 'page',
	});

	const { unhandledError, hasRestrictedError, hasNotFoundError, anonymousAccessQueryError } =
		errorProps;

	if (
		unhandledError ||
		hasRestrictedError ||
		hasNotFoundError ||
		anonymousAccessQueryError ||
		(!loading && !hasSpace)
	) {
		return (
			<ErrorView
				unhandledError={unhandledError || anonymousAccessQueryError}
				hasRestrictedError={hasRestrictedError}
				hasNotFoundError={hasNotFoundError}
			/>
		);
	}

	return (
		<>
			{routeFragmentRefs ? (
				<SpaceHeader
					spaceFragmentRef={routeFragmentRefs.space}
					isExternalCollaborator={Boolean(spaceDetails.isExternalCollaborator)}
				/>
			) : (
				<SpaceHeader {...spaceDetails} />
			)}
			<SidebarContent data-vc="space-navigation-sidebar-content">
				{!loading ? <ExperienceSuccess name={APP_NAV_CONTAINER_EXPERIENCE} /> : <Fragment />}

				<CommonSpaceNavigationContent
					showShortcutsSection={showShortcutsSection}
					space={space}
					spaceKey={spaceKey}
					quick={quick}
					homepageId={homepageId}
					loading={loading}
					isSpaceAdmin={isSpaceAdmin}
					isBlogNavigation={isBlogNavigation}
					blogsHidden={blogsHidden}
					pageTreeFinishedLoading={pageTreeFinishedLoading}
					onDragDropSuccess={onDragDropSuccess}
					onContentTreeLoadComplete={onContentTreeLoadComplete}
					shortcutsFragmentRef={routeFragmentRefs?.spaceSidebarLinks}
				/>

				<ul css={spaceLinksStyles} data-testid="space-links">
					{loading ? (
						<ContainerSkeleton header={false} />
					) : routeFragmentRefs ? (
						<SpaceLinks spaceSidebarLinksFragmentRef={routeFragmentRefs.spaceSidebarLinks} />
					) : (
						<SpaceLinks links={main} />
					)}
				</ul>

				{!loading && !loadingForgeApps && shouldRenderSpaceApps ? (
					<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
						<PageSegmentLoadStart metric={SPACE_APPS_METRIC} />
						<SpaceApps
							spaceKey={spaceKey}
							canAdminister={canAdminister}
							allAppLinks={allAppLinks}
							allVisibleAppLinks={allVisibleAppLinks}
						/>
					</ErrorBoundary>
				) : (
					<Fragment />
				)}
				{!loading &&
					canCreateContent &&
					UNSAFE_expValEqualsNoExposure(
						'confluence_autonomous_space_curation',
						'isEnabled',
						true,
					) && (
						<div css={relatedContentStyles.container}>
							{expValEquals(
								'confluence_autonomous_space_curation_ssr_streaming',
								'isEnabled',
								true,
							) ? (
								<SuspenseWithDataFetcher dataFetcherName="space-curation" fallback={null}>
									<SpaceNavigationRelatedContent spaceId={space?.id} />
								</SuspenseWithDataFetcher>
							) : (
								<SpaceNavigationRelatedContent spaceId={space?.id} />
							)}
						</div>
					)}
			</SidebarContent>
			{!loading &&
				(fg('confluence_frontend_tokenize_border') ? (
					<hr role="presentation" css={separatorStyles} id="space-header-top-divider" />
				) : (
					<hr role="presentation" css={separatorStylesFallback} id="space-header-top-divider" />
				))}
		</>
	);
};
